import L from "leaflet";
import geolayer from "@apis/geolayer";
import { svgIcon } from "@/components/Leaflet/Map/Marker/SvgIcon";
import SitemapIconDefault from "@images/default/sitemap_icon_default.svg";
import {
  PruneCluster,
  PruneClusterForLeaflet,
} from "@/components/Leaflet/Map/PruneCluster/PruneCluster";
import { ClusterIcon } from "@/components/Leaflet/Map/Marker/ClusterIcon";

L.SitemapLayer = L.FeatureGroup.extend({
  options: {},
  bounds: L.latLngBounds(),
  pruneCluster: null,

  updateData: function (orgId, options) {
    var self = this;

    // Initialize PruneCluster if not already done
    if (!self.pruneCluster) {
      self.pruneCluster = new PruneClusterForLeaflet(120, 20);

      // Set cluster to use shadowPane for lower z-index
      self.pruneCluster.options.pane = "shadowPane";

      PruneCluster.Cluster.ENABLE_MARKERS_LIST = true;
      self.pruneCluster.BuildLeafletClusterIcon = function (cluster) {
        let iconClassName = "cluster-svg-sitemap";
        let markers = cluster.GetClusterMarkers();
        let _length = markers.length;

        var width = 24,
          textSize = 11;
        if (_length >= 500 && _length < 5000) {
          width = 36;
          textSize = 7;
        } else if (_length >= 5000) {
          width = 44;
          textSize = 6;
        }

        // White hexagon with dark text for sitemap cluster
        const svgString = `<svg width="${width}" height="${width}" viewBox="0 0 22 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path id="cluster-bg" fill-rule="evenodd" clip-rule="evenodd" d="M11 0L21.7999 6V18L11 24L0.199951 18V6L11 0Z" fill="#FFFFFF" stroke="#212851" stroke-width="1"></path>
            <text id="cluster-text" x="50%" y="50%" font-size="${textSize}" font-weight="bold" transform="translate(0, 3.5)" text-anchor="middle" fill="#212851">${_length}</text>
            </svg>
        `;
        return ClusterIcon({
          count: _length,
          svgString: svgString,
          className: iconClassName,
        });
      };

      self.pruneCluster.PrepareLeafletMarker = function (leafletMarker, data) {
        leafletMarker.setIcon(data.icon);

        // Set marker to use shadowPane for lower z-index
        leafletMarker.options.pane = "shadowPane";

        if (data.tooltip) {
          leafletMarker.unbindTooltip();
          leafletMarker.bindTooltip(data.tooltip, {
            permanent: true,
            direction: "top",
            className: "transparent-tooltip",
            offset: [0, -8],
          });
        }
        leafletMarker.sitemapInfo = data.sitemapInfo;
      };

      // Add cluster layer to map if map is available
      if (self._map) {
        self._map.addLayer(self.pruneCluster);
      }
    }

    // Fetch site maps (IMAGE type geolayers)
    geolayer
      .getAllLayersForMapVisibility({
        orgId: orgId,
      })
      .then(
        ({ data }) => {
          self.clearLayers();

          // Clear existing markers from cluster
          self.pruneCluster.RemoveMarkers();

          // Filter only IMAGE type (site maps)
          const sitemaps = (data.data || []).filter(
            (layer) => layer.type?.name === "IMAGE" || layer.type === "IMAGE"
          );

          for (var i = 0; i < sitemaps.length; i++) {
            var sitemap = sitemaps[i];

            if (options && +options.ignoreSitemapId === sitemap.id) {
              continue;
            }

            // Get center point from the image data
            var centerPoint = self.calculateCenterPoint(sitemap.data);

            if (centerPoint) {
              // Extend bounds
              self.bounds.extend([centerPoint[0], centerPoint[1]]);

              // Create sitemap icon marker and add to cluster
              const icon = svgIcon({
                iconUrl: SitemapIconDefault,
                id: sitemap.id,
                state: "sitemap",
                iconAnchor: [10, 30],
              });

              const marker = new PruneCluster.Marker(
                centerPoint[0],
                centerPoint[1],
                {
                  icon: icon,
                  tooltip: sitemap.name,
                }
              );
              marker.data.sitemapInfo = sitemap;
              marker.getLatLng = function () {
                return new L.latLng(centerPoint[0], centerPoint[1]);
              };

              self.pruneCluster.RegisterMarker(marker);
            }
          }

          // Process the cluster view
          self.pruneCluster.ProcessView();
        },
        (err) => {
          console.error("Error loading sitemap data:", err);
        }
      );
  },

  calculateCenterPoint: function (imageData) {
    if (!imageData) return null;

    // IMAGE layer data has topLeft, topRight, bottomLeft coordinates
    const topLeft = imageData.topLeft;
    const topRight = imageData.topRight;
    const bottomLeft = imageData.bottomLeft;

    if (!topLeft || !topRight || !bottomLeft) return null;

    // Calculate center using diagonal: bottomRight = topRight + (bottomLeft - topLeft)
    const bottomRightLat = topRight.lat + (bottomLeft.lat - topLeft.lat);
    const bottomRightLng = topRight.lng + (bottomLeft.lng - topLeft.lng);

    const centerLat = (topLeft.lat + bottomRightLat) / 2;
    const centerLng = (topLeft.lng + bottomRightLng) / 2;

    return [centerLat, centerLng];
  },

  onAdd: function (map) {
    this._map = map;
    if (this.pruneCluster) {
      map.addLayer(this.pruneCluster);
    }
    L.FeatureGroup.prototype.onAdd.call(this, map);
  },

  onRemove: function (map) {
    this._map = null;
    if (this.pruneCluster) {
      map.removeLayer(this.pruneCluster);
    }
    L.FeatureGroup.prototype.onRemove.call(this, map);
  },

  getLayer: function () {
    return this;
  },

  getBounds: function () {
    return this.bounds;
  },
});

L.sitemapLayer = function (opts) {
  return new L.SitemapLayer(opts);
};

export const SitemapLayer = L.sitemapLayer;
